import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  X,
  Calendar,
  FileText,
  Check,
  XCircle,
  BellRing,
  Loader2,
  Trash2,
  ExternalLink,
  Landmark,
  Banknote,
  User,
  Pencil,
  Send,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ApprovalTimeline from "./components/ApprovalTimeline";
import AdvanceDocumentPaper, {
  STATUS_MAP,
  paymentMethodLabel,
} from "./AdvanceDocumentPaper";
import { formatDateVN } from "../../../utils/formatUtils";
import {
  getAdvanceDetail,
  remindAdvanceApprovers,
  settleAdvance,
  submitAdvanceRequest,
  approveAdvance,
  getAdvanceApproverCandidates,
} from "../../../api/finance/advance/advanceRequests";
import { openAdvanceAttachment } from "../../../utils/openCachedAttachment";
import {
  showErrorToast,
  showSuccessToast,
  showInputDialog,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";

const toSelectOptions = (candidates = []) =>
  candidates.map((c) => ({
    value: c.id,
    label: c.tag ? `${c.name} (${c.tag})` : c.name,
    meta: c,
  }));

const AdvanceDetailModal = ({
  isOpen,
  onClose,
  advanceId,
  onUpdateStatus,
  onDelete,
  onEdit,
  onReminded,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [remindLoading, setRemindLoading] = useState(false);
  const [settleLoading, setSettleLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [firstMeta, setFirstMeta] = useState(null);
  const [nextMeta, setNextMeta] = useState(null);
  const [selectedFirst, setSelectedFirst] = useState(null);
  const [selectedNext, setSelectedNext] = useState(null);
  const [franchiseFinalize, setFranchiseFinalize] = useState(false);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;

  useEffect(() => {
    if (!isOpen || !advanceId) return;
    const loadDetail = async () => {
      try {
        setLoading(true);
        const res = await getAdvanceDetail(advanceId);
        setData(res);
      } catch (err) {
        console.error("Load detail error:", err);
      } finally {
        setLoading(false);
      }
    };
    loadDetail();
  }, [isOpen, advanceId]);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setRejectReason("");
      setFirstMeta(null);
      setNextMeta(null);
      setSelectedFirst(null);
      setSelectedNext(null);
      setFranchiseFinalize(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !data) return;

    const loadCandidates = async () => {
      try {
        setCandidatesLoading(true);
        if (
          data.status === "draft" &&
          (data.can_current_user_submit || data.employee_id === currentUser?.id)
        ) {
          const meta = await getAdvanceApproverCandidates({
            purpose: "first",
            requester_id: data.employee_id,
          });
          setFirstMeta(meta);
          const opts = toSelectOptions(meta.candidates || []);
          setSelectedFirst(opts.length === 1 ? opts[0] : null);
        } else {
          setFirstMeta(null);
          setSelectedFirst(null);
        }

        if (data.can_current_user_approve) {
          const meta = await getAdvanceApproverCandidates({
            purpose: "next",
            advance_id: advanceId,
          });
          setNextMeta(meta);
          if (meta.auto_next) {
            setSelectedNext({
              value: meta.auto_next.id,
              label: meta.auto_next.tag
                ? `${meta.auto_next.name} (${meta.auto_next.tag})`
                : meta.auto_next.name,
            });
          } else {
            const opts = toSelectOptions(meta.candidates || []);
            setSelectedNext(opts.length === 1 ? opts[0] : null);
          }
          setFranchiseFinalize(false);
        } else {
          setNextMeta(null);
          setSelectedNext(null);
        }
      } catch (err) {
        console.error(err);
        showErrorToast(
          err?.response?.data?.message || "Không tải được danh sách duyệt."
        );
      } finally {
        setCandidatesLoading(false);
      }
    };

    loadCandidates();
  }, [isOpen, data, advanceId, currentUser?.id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const handleApprove = async () => {
    try {
      if (nextMeta?.stage !== "finalize") {
        if (nextMeta?.mode === "franchise" && franchiseFinalize) {
          // ok
        } else if (nextMeta?.required !== false || nextMeta?.mode === "hard") {
          if (
            nextMeta?.stage !== "finalize" &&
            !selectedNext?.value &&
            !nextMeta?.auto_next
          ) {
            showErrorToast("Vui lòng chọn người duyệt cấp tiếp theo.");
            return;
          }
        }
      }

      setActionLoading(true);
      const payload = {};
      if (nextMeta?.stage === "finalize") {
        // CEO cuối
      } else if (nextMeta?.mode === "franchise" && franchiseFinalize) {
        payload.finalize = true;
      } else {
        payload.next_approver_id =
          selectedNext?.value || nextMeta?.auto_next?.id || null;
      }

      await approveAdvance(advanceId, payload);
      showSuccessToast("Duyệt thành công");
      onUpdateStatus?.(advanceId, "submitted");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể duyệt.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Vui lòng nhập lý do từ chối");
      return;
    }
    try {
      setActionLoading(true);
      await onUpdateStatus(advanceId, "rejected", rejectReason);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const { label, cls } = STATUS_MAP[status] || {
      label: status,
      cls: "bg-gray-50 text-gray-600 border-gray-100",
    };
    return (
      <span
        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${cls}`}
      >
        {label}
      </span>
    );
  };

  const canSubmitDraft =
    data?.can_current_user_submit ||
    (data?.status === "draft" && data?.employee_id === currentUser?.id);

  const canEditDraft =
    data?.can_current_user_edit ||
    (data?.status === "draft" &&
      data?.employee_id === currentUser?.id &&
      hasPermission("finance.advance.create", "C"));

  const canCurrentUserRemind =
    data?.status === "waiting" && data?.employee_id === currentUser?.id;

  const canDeleteSelf =
    (data?.status === "draft" ||
      (data?.status === "waiting" && Number(data?.current_level) === 1)) &&
    data?.employee_id === currentUser?.id &&
    hasPermission("finance.advance.cancel_self", "D");

  const canSettle =
    data?.status === "approved" &&
    hasPermission("finance.advance.execute_payment", "A");

  const handleSubmitDraft = async () => {
    if (!advanceId) return;
    if (!selectedFirst?.value) {
      showErrorToast("Vui lòng chọn người duyệt cấp 1.");
      return;
    }
    const confirm = await showConfirmDialog({
      title: "Gửi duyệt đề nghị",
      text: `Gửi đến ${selectedFirst.label}? Phiếu sẽ chuyển sang Chờ duyệt.`,
      confirmText: "Gửi duyệt",
      cancelText: "Để sau",
    });
    if (!confirm?.isConfirmed) return;
    try {
      setSubmitLoading(true);
      await submitAdvanceRequest(advanceId, {
        first_approver_id: selectedFirst.value,
      });
      showSuccessToast("Đã gửi đề nghị đi duyệt.");
      onUpdateStatus?.(advanceId, "submitted");
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể gửi duyệt.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRemind = async () => {
    if (!advanceId) return;
    try {
      setRemindLoading(true);
      const res = await remindAdvanceApprovers(advanceId);
      showSuccessToast(
        res?.message || "Đã gửi thông báo nhắc duyệt đến người phê duyệt."
      );
      setData((prev) =>
        prev ? { ...prev, is_urgent: true, urgent_at: new Date().toISOString() } : prev
      );
      onReminded?.();
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể gửi nhắc duyệt");
    } finally {
      setRemindLoading(false);
    }
  };

  const handleSettle = async () => {
    if (!advanceId || !data) return;
    try {
      const amountText = await showInputDialog({
        title: "Quyết toán tạm ứng",
        inputLabel: `Số tiền quyết toán (₫). Đề nghị: ${Number(
          data.advance_amount || 0
        ).toLocaleString("vi-VN")}`,
        confirmText: "Quyết toán",
        inputPlaceholder: String(data.advance_amount || ""),
      });
      if (amountText == null) return;
      const settled_amount = Number(
        String(amountText || data.advance_amount).replace(/[^\d.]/g, "")
      );
      if (!settled_amount || settled_amount <= 0) {
        showErrorToast("Số tiền quyết toán không hợp lệ.");
        return;
      }
      setSettleLoading(true);
      await settleAdvance(advanceId, {
        settled_amount,
        settlement_type: "full",
      });
      showSuccessToast("Quyết toán thành công.");
      const refreshed = await getAdvanceDetail(advanceId);
      setData(refreshed);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Không thể quyết toán.");
    } finally {
      setSettleLoading(false);
    }
  };

  const lines =
    Array.isArray(data?.lines) && data.lines.length > 0
      ? data.lines
      : data?.advance_reason
        ? [{ line_no: 1, description: data.advance_reason, amount: data.advance_amount }]
        : [];

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex flex-col bg-white md:bg-slate-900/55 md:backdrop-blur-sm md:items-center md:justify-center md:p-5"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 36, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 36, scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full h-full min-h-0 md:max-w-6xl md:h-[90vh] md:rounded-[1.75rem] shadow-2xl overflow-hidden flex flex-col border-0 md:border md:border-slate-200"
        >
          <div className="px-4 py-3 md:px-6 md:py-4 border-b border-slate-200 flex items-center justify-between shrink-0 bg-white pt-[max(0.75rem,env(safe-area-inset-top))]">
            <div className="min-w-0">
              <h2 className="text-base md:text-lg font-black text-slate-800 uppercase tracking-tight">
                Chi tiết <span className="text-red-700">tạm ứng</span>
              </h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 truncate">
                {data?.request_code || "Phiếu đề nghị tạm ứng"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {data ? getStatusBadge(data.status) : null}
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 transition-colors"
                aria-label="Đóng"
              >
                <X size={22} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {loading && (
              <div className="flex justify-center items-center h-64">
                <Loader2 size={32} className="animate-spin text-red-600" />
              </div>
            )}

            {!loading && !data && (
              <p className="text-red-500 text-sm font-medium text-center py-16">
                Không có dữ liệu
              </p>
            )}

            {!loading && data && (
              <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
                {/* LEFT: administrative document — cuộn độc lập */}
                <div className="min-h-0 max-h-[38vh] lg:max-h-none flex-none lg:flex-1 overflow-y-auto overscroll-contain bg-slate-100/80 border-b lg:border-b-0 lg:border-r border-slate-200 p-4 md:p-6">
                  <div className="mx-auto w-full max-w-[640px] pb-4">
                    <AdvanceDocumentPaper data={data} lines={lines} />
                  </div>
                </div>

                {/* RIGHT: info + actions */}
                <div className="min-h-0 flex-1 lg:max-w-md lg:flex-none lg:w-[380px] xl:w-[420px] flex flex-col overflow-hidden bg-white">
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 md:px-5 md:py-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-700 flex items-center justify-center shrink-0 border border-red-100">
                        <User size={20} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-slate-800 truncate">
                          {data.employee_name}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          {[data.branch_name, data.department_name]
                            .filter(Boolean)
                            .join(" • ") || "—"}
                        </p>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-center">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Số tiền tạm ứng
                      </p>
                      <p className="text-2xl font-black text-red-700 mt-1 tracking-tight">
                        {Number(data.advance_amount || 0).toLocaleString("vi-VN")} ₫
                      </p>
                      {Number(data.settled_amount) > 0 && (
                        <p className="mt-2 text-xs font-bold text-emerald-600">
                          Đã quyết toán:{" "}
                          {Number(data.settled_amount).toLocaleString("vi-VN")} ₫
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <InfoRow icon={FileText} label="Mã phiếu" value={data.request_code} />
                      <InfoRow
                        icon={Calendar}
                        label="Ngày nhận tiền"
                        value={formatDateVN(data.request_date)}
                      />
                      <InfoRow
                        icon={Calendar}
                        label="Ngày hoàn"
                        value={formatDateVN(data.repayment_due_date)}
                      />
                      <InfoRow
                        label="Hình thức"
                        value={paymentMethodLabel(data.payment_method)}
                      />
                      <InfoRow
                        label="Cấp duyệt"
                        value={
                          data.current_level != null
                            ? `Cấp ${data.current_level}`
                            : data.status === "draft"
                              ? "Chưa gửi"
                              : "—"
                        }
                      />
                    </div>

                    {(data.bank_name || data.account_number) && (
                      <div className="rounded-2xl border border-slate-100 p-4 space-y-1.5">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                          <Landmark size={12} /> Nhận tiền
                        </p>
                        <p className="text-sm text-slate-700">
                          <span className="font-semibold">NH:</span>{" "}
                          {data.bank_name || "—"}
                        </p>
                        <p className="text-sm text-slate-700 font-mono">
                          <span className="font-semibold font-sans">STK:</span>{" "}
                          {data.account_number || "—"}
                        </p>
                        {data.account_holder ? (
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold">Chủ TK:</span>{" "}
                            {data.account_holder}
                          </p>
                        ) : null}
                      </div>
                    )}

                    {(data.pdf_url || data.attached_file_url) && (
                      <button
                        type="button"
                        onClick={() => openAdvanceAttachment(advanceId || data.id)}
                        className="inline-flex items-center gap-2 text-sm font-bold text-red-700 hover:underline"
                      >
                        <FileText size={14} />
                        Xem file đính kèm
                      </button>
                    )}

                    {data.approval_steps?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3">
                          Lịch sử phê duyệt
                        </p>
                        <ApprovalTimeline steps={data.approval_steps} />
                      </div>
                    )}

                    {canSubmitDraft && (
                      <div className="p-4 rounded-2xl border border-red-100 bg-red-50/40 space-y-3">
                        <p className="text-[11px] font-bold text-red-700 uppercase tracking-widest">
                          Chọn người duyệt cấp 1
                        </p>
                        {firstMeta?.hint ? (
                          <p className="text-xs text-slate-600">{firstMeta.hint}</p>
                        ) : null}
                        {candidatesLoading ? (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Loader2 size={16} className="animate-spin" /> Đang tải...
                          </div>
                        ) : (
                          <Select
                            value={selectedFirst}
                            onChange={setSelectedFirst}
                            options={toSelectOptions(firstMeta?.candidates || [])}
                            styles={customSelectStyles}
                            placeholder="Chọn người duyệt..."
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        )}
                      </div>
                    )}

                    {data.can_current_user_approve && (
                      <div className="p-4 rounded-2xl border border-amber-100 bg-amber-50/50 space-y-3">
                        <p className="text-[11px] font-bold text-amber-800 uppercase tracking-widest">
                          Cấp duyệt tiếp theo
                        </p>
                        {nextMeta?.hint ? (
                          <p className="text-xs text-slate-600">{nextMeta.hint}</p>
                        ) : null}
                        {nextMeta?.stage === "finalize" ? (
                          <p className="text-sm font-semibold text-emerald-700">
                            Bạn là cấp cuối — duyệt sẽ hoàn tất phiếu.
                          </p>
                        ) : candidatesLoading ? (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Loader2 size={16} className="animate-spin" /> Đang tải...
                          </div>
                        ) : (
                          <>
                            {!franchiseFinalize && (
                              <Select
                                value={selectedNext}
                                onChange={(opt) => {
                                  setSelectedNext(opt);
                                  setFranchiseFinalize(false);
                                }}
                                options={toSelectOptions(nextMeta?.candidates || [])}
                                styles={customSelectStyles}
                                placeholder="Chọn người duyệt tiếp..."
                                isSearchable
                                isDisabled={
                                  !!nextMeta?.auto_next && nextMeta?.stage === "ceo"
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                              />
                            )}
                            {nextMeta?.mode === "franchise" &&
                              nextMeta?.can_finalize && (
                                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={franchiseFinalize}
                                    onChange={(e) => {
                                      setFranchiseFinalize(e.target.checked);
                                      if (e.target.checked) setSelectedNext(null);
                                    }}
                                  />
                                  Kết thúc chuỗi duyệt tại đây
                                </label>
                              )}
                          </>
                        )}
                      </div>
                    )}

                    {data.can_current_user_approve && (
                      <div>
                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2">
                          Lý do từ chối
                        </label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={3}
                          placeholder="Bắt buộc khi từ chối..."
                          className="w-full border border-slate-200 rounded-2xl p-3 text-sm font-medium focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                        />
                      </div>
                    )}
                  </div>

                  {/* Actions sticky — luôn hiện trong khung phải */}
                  <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 md:px-5 md:py-4 safe-area-pb z-10">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {canDeleteSelf && (
                        <IconActionButton
                          title="Xóa phiếu"
                          disabled={actionLoading}
                          onClick={() => onDelete?.(advanceId)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={18} />
                        </IconActionButton>
                      )}
                      {canEditDraft && (
                        <IconActionButton
                          title="Sửa phiếu"
                          disabled={actionLoading}
                          onClick={() => onEdit?.(advanceId, data)}
                          className="border-slate-200 text-slate-600 hover:bg-slate-50"
                        >
                          <Pencil size={18} />
                        </IconActionButton>
                      )}
                      {canCurrentUserRemind && (
                        <IconActionButton
                          title="Nhắc duyệt"
                          disabled={remindLoading}
                          onClick={handleRemind}
                          className="border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100"
                        >
                          {remindLoading ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <BellRing size={18} />
                          )}
                        </IconActionButton>
                      )}
                      {canSettle && (
                        <IconActionButton
                          title="Quyết toán"
                          disabled={settleLoading}
                          onClick={handleSettle}
                          className="border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                        >
                          {settleLoading ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : (
                            <Banknote size={18} />
                          )}
                        </IconActionButton>
                      )}
                      {data.status === "approved" && (
                        <Link
                          to="/finance/income_expenditure"
                          title="Mở Thu — Chi"
                          aria-label="Mở Thu — Chi"
                          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all shrink-0"
                          onClick={onClose}
                        >
                          <ExternalLink size={18} />
                        </Link>
                      )}

                      <div className="flex flex-wrap items-center gap-1.5 ml-auto">
                        {data.can_current_user_approve && (
                          <IconActionButton
                            title="Từ chối"
                            disabled={actionLoading}
                            onClick={handleReject}
                            className="border-red-200 text-red-600 bg-red-50 hover:bg-red-100"
                          >
                            {actionLoading ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <XCircle size={18} />
                            )}
                          </IconActionButton>
                        )}
                        {canSubmitDraft && (
                          <button
                            type="button"
                            title="Gửi duyệt"
                            disabled={submitLoading || !selectedFirst?.value}
                            onClick={handleSubmitDraft}
                            className="inline-flex h-10 items-center gap-1.5 px-3 rounded-xl font-bold bg-red-700 text-white hover:bg-red-800 transition-all text-xs tracking-wide disabled:opacity-50 shrink-0"
                          >
                            {submitLoading ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Send size={16} />
                            )}
                            Gửi duyệt
                          </button>
                        )}
                        {data.can_current_user_approve && (
                          <button
                            type="button"
                            title="Phê duyệt"
                            disabled={actionLoading}
                            onClick={handleApprove}
                            className="inline-flex h-10 items-center gap-1.5 px-3 rounded-xl font-bold bg-red-700 text-white hover:bg-red-800 transition-all text-xs tracking-wide disabled:opacity-50 shrink-0 shadow-md shadow-red-100"
                          >
                            {actionLoading ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Check size={16} />
                            )}
                            Duyệt
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

const IconActionButton = ({
  title,
  onClick,
  disabled,
  className = "",
  children,
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border bg-white transition-all disabled:opacity-50 shrink-0 ${className}`}
  >
    {children}
  </button>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start justify-between gap-3 rounded-xl border-2 border-slate-200 bg-white px-3.5 py-3">
    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5 shrink-0 max-w-[42%]">
      {Icon ? <Icon size={12} className="shrink-0" /> : null}
      <span>{label}</span>
    </p>
    <p className="text-sm font-semibold text-slate-800 text-right break-words min-w-0 flex-1">
      {value || "—"}
    </p>
  </div>
);

export default AdvanceDetailModal;
